import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";
import { authorizedFetch, parseResponse } from "./api";
import { authCacheScope } from "./cacheScope";
import type { AnalysisRelease } from "./analysisRuns";

export interface HypnogramResult {
  state: string;
  recording_ref: string;
  data_version?: string | null;
  processing_version?: string | null;
  model?: { id: string; sha256: string };
  encoding?: string;
  epoch_seconds?: number;
  start_seconds?: number;
  labels?: string[];
  stages?: Array<number | null>;
  unscored_tail_seconds?: number;
  reason_code?: string;
  analysis_run_ref?: string;
  algorithm_release?: Pick<AnalysisRelease, "release_id" | "algorithm_id" | "display_name"> & {
    algorithm_version?: string;
  };
  finished_at?: string | null;
  promoted_at?: string | null;
  explicitly_promoted?: boolean;
}

export interface HistoricalHypnogram {
  run_ref: string;
  input_data_version: string;
  input_manifest_sha256: string;
  duration_seconds: number;
  algorithm_release: AnalysisRelease;
  finished_at?: string | null;
  result: HypnogramResult;
}

export const hypnogramKeys = {
  current: (recording: string, dataVersion: string, runRef: string | null) =>
    ["operational-hypnogram", authCacheScope(), recording, dataVersion, runRef] as const,
  historical: (recording: string, runRef: string) =>
    ["analysis-hypnogram", authCacheScope(), recording, runRef] as const,
};

function recordingRoute(recording: string): string {
  if (!PORTABLE_RECORDING_REF_PATTERN.test(recording)) throw new TypeError("Invalid recording reference");
  return `/platform/mobile/v1/recordings/${encodeURIComponent(recording)}`;
}

export async function getCurrentHypnogram(recording: string, signal?: AbortSignal): Promise<HypnogramResult> {
  return parseResponse(await authorizedFetch(recordingRoute(recording) + "/hypnogram", { signal }));
}

export async function getHistoricalHypnogram(recording: string, run: string,
  signal?: AbortSignal): Promise<HistoricalHypnogram> {
  return parseResponse(await authorizedFetch(recordingRoute(recording) +
    `/analyses/${encodeURIComponent(run)}/hypnogram`, { signal }));
}
